import type { LoggerEvent } from '@/types/logger.types'

import type { HeaderBidding } from './header-bidding/prebid'
import type { HbAdUnitDefinition, HbAuctionContext, HeaderBiddingAuctionResult } from './header-bidding/prebid/types'
import { isOwnerTrackedAuction } from './providers/shared/owner-tracker'
import type { AdType } from './types'
import type { AdConsoleLogger } from './utils/ad-console-logger'

type PendingAuctionResult = Promise<HeaderBiddingAuctionResult | undefined> | undefined

export class AuctionTracker {
    private readonly results: PendingAuctionResult[]

    constructor(
        size: number,
        private readonly options: {
            headerBidding: HeaderBidding | undefined
            logEvent: (event: LoggerEvent) => void
            adConsoleLogger: AdConsoleLogger
        },
    ) {
        this.results = Array.from({ length: size })
    }

    async resolveAuction(
        index: number,
        hbAdUnit: HbAdUnitDefinition | undefined,
        context: HbAuctionContext,
        allowFallbackReuse: boolean,
    ): Promise<HeaderBiddingAuctionResult | undefined> {
        if (allowFallbackReuse) {
            const fallback = await this.getBestAuction(context.placement, index)
            if (fallback) {
                return fallback
            }
        }

        if (!hbAdUnit) {
            return undefined
        }

        this.results[index] = this.options.headerBidding?.runAuction(hbAdUnit, context)
        this.results[index]?.catch(() => {})
        return this.results[index]
    }

    private async getBestAuction(placement: AdType, ownIndex: number): Promise<HeaderBiddingAuctionResult | undefined> {
        const all = await Promise.all(
            this.results.map(async (pending, index) => ({ index, result: await pending?.catch(() => undefined) })),
        )
        this.options.adConsoleLogger.debug(`fallback candidates for waterfall step ${ownIndex + 1}`, all)

        const usable = all
            .filter(
                (candidate): candidate is { index: number; result: HeaderBiddingAuctionResult } =>
                    !!candidate.result?.winningBid &&
                    !candidate.result.renderedBid &&
                    candidate.result.winningBid.status !== 'rendered' &&
                    Date.now() <
                        candidate.result.winningBid.responseTimestamp + candidate.result.winningBid.ttl * 1000 &&
                    !(
                        candidate.result.winningBid.bidder === 'playgama' &&
                        candidate.result.context &&
                        isOwnerTrackedAuction(candidate.result.context.provider, candidate.result.context.placement)
                    ),
            )
            .toSorted((a, b) => (b.result.winningBid?.cpm ?? 0) - (a.result.winningBid?.cpm ?? 0))

        this.options.adConsoleLogger.debug(`usable fallback candidates for waterfall step ${ownIndex + 1}`, usable)

        const best = usable[0]

        this.options.adConsoleLogger.debug(
            best
                ? `fallback bid found for waterfall step ${ownIndex + 1}`
                : `no fallback bid found for waterfall step ${ownIndex + 1}`,
            best,
        )

        if (best) {
            this.options.logEvent({
                event: 'custom_event',
                eventName: 'ad_hb_fallback_bid_used',
                adPlacement: placement,
                waterfallIndex: ownIndex + 1,
                dimension3: `${best.index + 1}`,
            })
        }

        return best?.result
    }
}
